import React from "react";
import { Link } from "react-router-dom";

/**
 * Optimized Breadcrumbs Component
 *
 * Props:
 * - maintitle: Main page title
 * - title: First breadcrumb (usually "Dashboard")
 * - subtitle: Second breadcrumb (current page)
 * - showButton: Set to false to hide button section
 * - addButton: Function that returns custom button JSX
 * - modal: Modal target ID (e.g., "#add_leave")
 * - name: Button text (e.g., "Add New")
 * - link: Button link (for navigation instead of modal)
 * - buttons: Array of button configs for multiple buttons
 */
const Breadcrumbs = ({
  maintitle,
  title = "Dashboard",
  subtitle,
  showButton = true,
  addButton = null,
  modal = null,
  name = "Add New",
  link = null,
  buttons = null,
  exportButtons = false,
}) => {
  // If addButton function is provided, use it (highest priority)
  if (typeof addButton === 'function') {
    return (
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">{maintitle}</h3>
            {title && (
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{title}</Link>
                </li>
                {subtitle && (
                  <li className="breadcrumb-item active">{subtitle}</li>
                )}
              </ul>
            )}
          </div>
          <div className="col-auto float-end ms-auto">
            {addButton()}
          </div>
        </div>
      </div>
    );
  }

  // No button section
  if (showButton === false) {
    return (
      <div className="page-header">
        <div className="row">
          <div className="col-sm-12">
            <h3 className="page-title">{maintitle}</h3>
          </div>
        </div>
      </div>
    );
  }

  // Export buttons (CSV, PDF, Print)
  if (exportButtons) {
    return (
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">{maintitle}</h3>
            {title && (
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{title}</Link>
                </li>
                {subtitle && (
                  <li className="breadcrumb-item active">{subtitle}</li>
                )}
              </ul>
            )}
          </div>
          <div className="col-auto float-end ms-auto">
            <div className="btn-group btn-group-sm">
              <button className="btn btn-white">CSV</button>
              <button className="btn btn-white">PDF</button>
              <button className="btn btn-white">
                <i className="fa-solid fa-print fa-lg" /> Print
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Multiple custom buttons
  if (buttons && Array.isArray(buttons)) {
    return (
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">{maintitle}</h3>
            {title && (
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{title}</Link>
                </li>
                {subtitle && (
                  <li className="breadcrumb-item active">{subtitle}</li>
                )}
              </ul>
            )}
          </div>
          <div className="col-auto float-end ms-auto">
            {buttons.map((btn, index) => (
              <Link
                key={index}
                to={btn.link || "#"}
                className={btn.className || "btn add-btn me-2"}
                data-bs-toggle={btn.modal ? "modal" : undefined}
                data-bs-target={btn.modal}
                onClick={btn.onClick}
              >
                {btn.icon && <i className={btn.icon} />}
                {btn.text}
              </Link>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Single button (modal or link)
  if (modal || link) {
    return (
      <div className="page-header">
        <div className="row align-items-center">
          <div className="col">
            <h3 className="page-title">{maintitle}</h3>
            {title && (
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{title}</Link>
                </li>
                {subtitle && (
                  <li className="breadcrumb-item active">{subtitle}</li>
                )}
              </ul>
            )}
          </div>
          <div className="col-auto float-end ms-auto">
            <Link
              to={link || "#"}
              className="btn add-btn"
              data-bs-toggle={modal ? "modal" : undefined}
              data-bs-target={modal}
            >
              <i className="fa fa-plus" /> {name}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Default: just breadcrumbs, no buttons
  return (
    <div className="page-header">
      <div className="row">
        <div className="col">
          <h3 className="page-title">{maintitle}</h3>
          {title && (
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">{title}</Link>
              </li>
              {subtitle && (
                <li className="breadcrumb-item active">{subtitle}</li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Breadcrumbs;
